import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FiPlus, FiTrash2, FiRefreshCw, FiFilter, FiSearch, FiX, FiEdit2, FiSave, FiAlertTriangle, FiCopy } from 'react-icons/fi';
import { faturamentoService, filterDataService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

import BIMultiSelectSearch from '../common/BIMultiSelectSearch';
import BISingleSelectSearch from '../common/BISingleSelectSearch';
import BIEmployeeSelectSearch from '../common/BIEmployeeSelectSearch';
import Loading from '../common/Loading';
import BIPagination from '../common/BIPagination';

const SingleFilterSelect = BISingleSelectSearch;
const MultiSelectSearch = BIMultiSelectSearch;


const formatCurrencyBRL = (raw) => {
  const digits = raw.replace(/\D/g, '');
  if (!digits) return '';
  const padded = digits.padStart(3, '0');
  const intPart = padded.slice(0, -2).replace(/^0+/, '') || '0';
  const decPart = padded.slice(-2);
  const formattedInt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return `${formattedInt},${decPart}`;
};

const parseCurrencyBRL = (formatted) => {
  if (!formatted) return '';
  return formatted.replace(/\./g, '').replace(',', '.');
};

const formatInitialAmount = (value) => {
  if (!value && value !== 0) return '';
  return formatCurrencyBRL(String(Math.round(Number(value) * 100)));
};

const EmployeeSelectSearch = BIEmployeeSelectSearch;

function Filtros() {
  const { hasSectionAccess } = useAuth();
  const canManage = hasSectionAccess('filtros', 'manage');
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterBranchSearch, setFilterBranchSearch] = useState('');
  const [filterVendorSearch, setFilterVendorSearch] = useState('');
  const [selectedFilterIds, setSelectedFilterIds] = useState([]);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  // Estados para modais de confirmação
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [filterToDelete, setFilterToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(50);
  const [totalRecords, setTotalRecords] = useState(0);
  const [sortField, setSortField] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const searchTimerRef = useRef(null);


  const hasActiveFilters = Boolean(searchTerm || filterBranchSearch || filterVendorSearch);

  const handleBranchChange = (val) => {
    setFilterBranchSearch(val);
    setPage(1);
  };

  const handleVendorChange = (val) => {
    setFilterVendorSearch(val);
    setPage(1);
  };

  const handleSearchChange = (val) => {
    setSearchTerm(val);
    setPage(1);
  };

  const handleClearAllFilters = () => {
    setSearchTerm('');
    setFilterBranchSearch('');
    setFilterVendorSearch('');
    setPage(1);
  };

  const parseCodes = (csv) =>
    csv
      ? csv
          .split(',')
          .filter((v) => v)
          .map((v) => v.trim())
      : [];
  const mergeCodes = (existing, extra) => {
    const current = parseCodes(existing);
    const next = parseCodes(extra);

    return Array.from(new Set([...current, ...next])).join(',');
  };
  const [form, setForm] = useState({
    id: null,
    name: '',
    description: '',
    filter_branch: '',
    filter_department: '',
    filter_activity: '',
    filter_section: '',
    filter_category: '',
    filter_brand: '',
    filter_product_code: '',
    filter_vendor: '',
    filter_employee: '',
    inherit_vendor_from_goal: false,
    inherit_branch_from_goal: false,
    inherit_branch_from_preset: false,
    inherit_employee_from_goal: false,
    commission_min_products_qty: '',
    commission_min_sold_value: '',
    commission_use_goal_value: false,
    commission_percent: '',
    commission_fixed_per_unit: '',
    show_commission_percentage: true,
    include_in_goal_total: true,
    include_commission_in_goal_total: true,
    include_value_if_min_achieved: false,
    available_for_goals: false,
    bonus_min_products_qty: '',
    bonus_min_sold_value: '',
    bonus_amount: '',
    start_date: '',
    end_date: ''
  });

  const load = useCallback(async (params = {}) => {
    setLoading(true);
    try {
      const res = await faturamentoService.listSavedFilters({ sort_by: sortField, sort_order: sortOrder, ...params });
      setList(res?.data?.filters || []);
      setTotalRecords(res?.data?.total || 0);
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Erro ao salvar';
      if (showModal) {
        setErrorMessage(errorMsg);
        setShowErrorModal(true);
      } else {
        setMessage({ type: 'danger', text: errorMsg });
      }
    } finally {
      setLoading(false);
    }
  }, [sortField, sortOrder]);

  useEffect(() => {
    if (searchTerm) {
      load({ page: 1, per_page: 0, search: searchTerm });
    } else {
      load({ page: 1, per_page: perPage });
    }
  }, [load]);

  const handlePageChange = (newPage) => {
    setPage(newPage);
    load({ page: newPage, per_page: perPage });
  };

  const totalPages = Math.ceil(totalRecords / perPage) || 1;

  useEffect(() => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      if (searchTerm) {
        load({ page: 1, per_page: 0, search: searchTerm });
      } else {
        setPage(1);
        load({ page: 1, per_page: perPage });
      }
    }, 400);
    return () => { if (searchTimerRef.current) clearTimeout(searchTimerRef.current); };
  }, [searchTerm]);

  const handleSort = (field) => {
    if (sortField === field) {
      const newOrder = sortOrder === 'asc' ? 'desc' : 'asc';
      setSortOrder(newOrder);
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const renderSortIcon = (field) => {
    if (sortField !== field) return <span style={{ marginLeft: 4, opacity: 0.2, fontSize: 10, userSelect: 'none' }}>▲▼</span>;
    return <span style={{ marginLeft: 4, fontSize: 10, userSelect: 'none' }}>{sortOrder === 'asc' ? '▲' : '▼'}</span>;
  };

  const reset = () => {
    setForm({
      id: null,
      name: '',
      description: '',
      filter_branch: '',
      filter_department: '',
      filter_activity: '',
      filter_section: '',
      filter_category: '',
      filter_brand: '',
      filter_product_code: '',
      filter_vendor: '',
      filter_employee: '',
      inherit_vendor_from_goal: false,
      inherit_branch_from_goal: false,
      inherit_branch_from_preset: false,
      inherit_employee_from_goal: false,
      commission_min_products_qty: '',
      commission_min_sold_value: '',
      commission_use_goal_value: false,
      commission_percent: '',
      commission_fixed_per_unit: '',
      show_commission_percentage: true,
      include_in_goal_total: true,
      include_commission_in_goal_total: true,
      include_value_if_min_achieved: false,
      available_for_goals: false,
      bonus_min_products_qty: '',
      bonus_min_sold_value: '',
      bonus_amount: '',
      start_date: '',
      end_date: ''
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    setMessage(null);
    
    // Validação: campos obrigatórios
    if (!form.name || form.name.trim() === '') {
      setErrorMessage('O campo Nome é obrigatório.');
      setShowErrorModal(true);
      return;
    }
    
    if ((!form.filter_branch || form.filter_branch.trim() === '') && !form.inherit_branch_from_goal && !form.inherit_branch_from_preset) {
      setErrorMessage('Informe ao menos uma filial ou marque uma opção para herdar a filial (do vendedor ou do Grupo de Meta).');
      setShowErrorModal(true);
      return;
    }
    
    try {
      const payload = {
        name: form.name.trim(),
        description: form.description.trim() || null,
        filter_branch: form.filter_branch.trim() || null,
        filter_department: form.filter_department.trim() || null,
        filter_activity: form.filter_activity.trim() || null,
        filter_section: form.filter_section.trim() || null,
        filter_category: form.filter_category.trim() || null,
        filter_brand: form.filter_brand.trim() || null,
        filter_product_code: form.filter_product_code.trim() || null,
        filter_vendor: form.filter_vendor.trim() || null,
        filter_employee: form.filter_employee.trim() || null,
        inherit_vendor_from_goal: Boolean(form.inherit_vendor_from_goal),
        inherit_branch_from_goal: Boolean(form.inherit_branch_from_goal),
        inherit_branch_from_preset: Boolean(form.inherit_branch_from_preset),
        inherit_employee_from_goal: Boolean(form.inherit_employee_from_goal),
        commission_min_products_qty: form.commission_min_products_qty === '' ? 0 : parseFloat(form.commission_min_products_qty),
        commission_min_sold_value: form.commission_min_sold_value === '' ? 0 : parseFloat(parseCurrencyBRL(String(form.commission_min_sold_value))),
        commission_use_goal_value: Boolean(form.commission_use_goal_value),
        commission_percent: form.commission_percent === '' ? 0 : parseFloat(form.commission_percent),
        commission_fixed_per_unit: form.commission_fixed_per_unit === '' ? 0 : parseFloat(parseCurrencyBRL(String(form.commission_fixed_per_unit))),
        show_commission_percentage: Boolean(form.show_commission_percentage),
        include_in_goal_total: Boolean(form.include_in_goal_total),
        include_commission_in_goal_total: Boolean(form.include_commission_in_goal_total),
        include_value_if_min_achieved: Boolean(form.include_value_if_min_achieved),
        available_for_goals: Boolean(form.available_for_goals),
        bonus_min_products_qty: form.bonus_min_products_qty === '' ? null : form.bonus_min_products_qty,
        bonus_min_sold_value: form.bonus_min_sold_value === '' ? null : parseFloat(parseCurrencyBRL(String(form.bonus_min_sold_value))),
        bonus_amount: form.bonus_amount === '' ? null : parseFloat(parseCurrencyBRL(String(form.bonus_amount))),
        start_date: form.start_date || null,
        end_date: form.end_date || null
      };
      if (form.id) {
        await faturamentoService.updateSavedFilter(form.id, payload);
        setMessage({ type: 'success', text: 'Filtro atualizado.' });
      } else {
        await faturamentoService.createSavedFilter(payload);
        setMessage({ type: 'success', text: 'Filtro salvo.' });
      }
      setShowModal(false);
      reset();
      setPage(1);
      await load({ page: 1, per_page: perPage });
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Erro ao salvar';
      if (showModal) {
        setErrorMessage(errorMsg);
        setShowErrorModal(true);
      } else {
        setMessage({ type: 'danger', text: errorMsg });
      }
    }
  };

  const edit = (f) => {
    setForm({
      id: f.id,
      name: f.name || '',
      description: f.description || '',
      filter_branch: f.filter_branch || '',
      filter_department: f.filter_department || '',
      filter_activity: f.filter_activity || '',
      filter_section: f.filter_section || '',
      filter_category: f.filter_category || '',
      filter_brand: f.filter_brand || '',
      filter_product_code: f.filter_product_code || '',
      filter_vendor: f.filter_vendor || '',
      filter_employee: f.filter_employee || '',
      inherit_vendor_from_goal: Boolean(f.inherit_vendor_from_goal),
      inherit_branch_from_goal: Boolean(f.inherit_branch_from_goal),
      inherit_branch_from_preset: Boolean(f.inherit_branch_from_preset),
      inherit_employee_from_goal: Boolean(f.inherit_employee_from_goal),
      commission_min_products_qty: f.commission_min_products_qty ?? '',
      commission_min_sold_value: formatInitialAmount(f.commission_min_sold_value),
      commission_use_goal_value: Boolean(f.commission_use_goal_value),
      commission_percent: f.commission_percent ?? '',
      commission_fixed_per_unit: formatInitialAmount(f.commission_fixed_per_unit),
      show_commission_percentage: f.show_commission_percentage !== undefined ? Boolean(f.show_commission_percentage) : true,
      include_in_goal_total: f.include_in_goal_total !== undefined ? Boolean(f.include_in_goal_total) : true,
      include_commission_in_goal_total: f.include_commission_in_goal_total !== undefined ? Boolean(f.include_commission_in_goal_total) : true,
      include_value_if_min_achieved: f.include_value_if_min_achieved !== undefined ? (Number(f.include_value_if_min_achieved) === 1 || f.include_value_if_min_achieved === true || f.include_value_if_min_achieved === '1') : false,
      available_for_goals: f.available_for_goals !== undefined ? (Number(f.available_for_goals) === 1 || f.available_for_goals === true || f.available_for_goals === '1') : true,
      bonus_min_products_qty: f.bonus_min_products_qty ?? '',
      bonus_min_sold_value: formatInitialAmount(f.bonus_min_sold_value),
      bonus_amount: formatInitialAmount(f.bonus_amount),
      start_date: f.start_date ? String(f.start_date).slice(0, 10) : '',
      end_date: f.end_date ? String(f.end_date).slice(0, 10) : ''
    });
    setShowModal(true);
  };

  const replicate = (f) => {
    setForm({
      id: null,
      name: `${f.name || ''} (Cópia)`,
      description: f.description || '',
      filter_branch: f.filter_branch || '',
      filter_department: f.filter_department || '',
      filter_activity: f.filter_activity || '',
      filter_section: f.filter_section || '',
      filter_category: f.filter_category || '',
      filter_brand: f.filter_brand || '',
      filter_product_code: f.filter_product_code || '',
      filter_vendor: f.filter_vendor || '',
      filter_employee: f.filter_employee || '',
      inherit_vendor_from_goal: Boolean(f.inherit_vendor_from_goal),
      inherit_branch_from_goal: Boolean(f.inherit_branch_from_goal),
      inherit_branch_from_preset: Boolean(f.inherit_branch_from_preset),
      inherit_employee_from_goal: Boolean(f.inherit_employee_from_goal),
      commission_min_products_qty: f.commission_min_products_qty ?? '',
      commission_min_sold_value: formatInitialAmount(f.commission_min_sold_value),
      commission_use_goal_value: Boolean(f.commission_use_goal_value),
      commission_percent: f.commission_percent ?? '',
      commission_fixed_per_unit: formatInitialAmount(f.commission_fixed_per_unit),
      show_commission_percentage: f.show_commission_percentage !== undefined ? Boolean(f.show_commission_percentage) : true,
      include_in_goal_total: f.include_in_goal_total !== undefined ? Boolean(f.include_in_goal_total) : true,
      include_commission_in_goal_total: f.include_commission_in_goal_total !== undefined ? Boolean(f.include_commission_in_goal_total) : true,
      include_value_if_min_achieved: f.include_value_if_min_achieved !== undefined ? (Number(f.include_value_if_min_achieved) === 1 || f.include_value_if_min_achieved === true || f.include_value_if_min_achieved === '1') : false,
      available_for_goals: f.available_for_goals !== undefined ? (Number(f.available_for_goals) === 1 || f.available_for_goals === true || f.available_for_goals === '1') : true,
      bonus_min_products_qty: f.bonus_min_products_qty ?? '',
      bonus_min_sold_value: formatInitialAmount(f.bonus_min_sold_value),
      bonus_amount: formatInitialAmount(f.bonus_amount),
      start_date: f.start_date ? String(f.start_date).slice(0, 10) : '',
      end_date: f.end_date ? String(f.end_date).slice(0, 10) : ''
    });
    setShowModal(true);
  };

  const openNewModal = () => {
    reset();
    setShowModal(true);
  };

  const toggleSelectOne = (id) => {
    setSelectedFilterIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = (filteredItems) => {
    if (selectedFilterIds.length === filteredItems.length && filteredItems.length > 0) {
      setSelectedFilterIds([]);
    } else {
      setSelectedFilterIds(filteredItems.map((f) => f.id));
    }
  };

  const confirmBulkDelete = async () => {
    setBulkDeleting(true);
    let successCount = 0;
    let errorCount = 0;

    try {
      for (const id of selectedFilterIds) {
        try {
          await faturamentoService.deleteSavedFilter(id);
          successCount++;
        } catch (e) {
          console.error(`Erro ao excluir filtro ${id}:`, e);
          errorCount++;
        }
      }

      if (errorCount === 0) {
        setMessage({ type: 'success', text: `${successCount} filtros excluídos com sucesso.` });
      } else {
        setMessage({ type: 'warning', text: `${successCount} filtros excluídos, ${errorCount} falharam.` });
      }
      
      setShowBulkDeleteModal(false);
      setSelectedFilterIds([]);
      setPage(1);
      load({ page: 1, per_page: perPage });
    } catch (e) {
      setMessage({ type: 'danger', text: 'Erro ao excluir lote de filtros.' });
    } finally {
      setBulkDeleting(false);
    }
  };

  const filteredList = list.filter(f => {
    const terms = searchTerm.toLowerCase().split(/\s+/).filter(Boolean);
    const matchesSearch = terms.length === 0 || terms.every(term =>
      f.name?.toLowerCase().includes(term) ||
      f.description?.toLowerCase().includes(term)
    );
    const matchesBranch = !filterBranchSearch ||
      filterBranchSearch.split(',').filter(Boolean).some(selectedCode =>
        (f.filter_branch || '').split(',').some(code =>
          code.trim() === selectedCode.trim()
        )
      );
    const matchesVendor = !filterVendorSearch ||
      (f.filter_vendor || '').split(',').some(code =>
        code.trim() === filterVendorSearch
      );
    return matchesSearch && matchesBranch && matchesVendor;
  });

  const remove = async (id) => {
    const filter = list.find(f => f.id === id);
    setFilterToDelete(filter);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!filterToDelete) return;
    
    setDeleting(true);
    try {
      await faturamentoService.deleteSavedFilter(filterToDelete.id);
      setMessage({ type: 'success', text: `Filtro "${filterToDelete.name}" removido com sucesso!` });
      setShowDeleteModal(false);
      setFilterToDelete(null);
      if (form.id === filterToDelete.id) {
        reset();
      }
      setPage(1);
      load({ page: 1, per_page: perPage });
    } catch (error) {
      const errorMsg = error.response?.data?.error || 'Erro ao remover filtro.';
      setErrorMessage(errorMsg);
      setShowErrorModal(true);
    } finally {
      setDeleting(false);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    if (!form.id) reset();
  };

  return (
    <div className="container-fluid py-4">
      <div className="mb-4 d-flex justify-content-between align-items-start flex-wrap gap-3">
        <div>
          <h2 className="mb-1">Filtros</h2>
          <p className="text-muted mb-0">
            Conjuntos salvos para obtenção de dados de vendas.
          </p>
        </div>
        <button type="button" className="btn-modern btn-modern-light" onClick={() => load({ page, per_page: perPage })} disabled={loading}>
          <FiRefreshCw size={16} className={loading ? 'spin-ico' : ''} />
        </button>
      </div>

      {message && <div className={`alert alert-${message.type} py-2 mb-4`}>{message.text}</div>}

      {/* Botao Novo Filtro */}
      {canManage && (
        <div className="mb-4">
          <button type="button" className="btn-modern btn-modern-primary" onClick={openNewModal}>
            <FiPlus size={16} className="me-2" />Novo Filtro
          </button>
        </div>
      )}

      {/* Listagem de Filtros */}
      <div className="settings-container">
        <div className="card-modern shadow-sm-modern" style={{ overflow: 'visible' }}>
          <div className="card-header-modern bg-transparent border-0 pt-4 px-4 pb-3 d-flex justify-content-between align-items-center flex-wrap gap-3" style={{ position: 'relative', zIndex: 100 }}>
            <div>
              <h5 className="mb-0 fw-bold text-dark">Filtros cadastrados</h5>
              <p className="text-muted small mb-0">Lista ordenada por nome</p>
            </div>
            
            <div className="d-flex align-items-center gap-3 flex-wrap filtros-filter-toolbar" style={{ position: 'relative', zIndex: 100 }}>
              <div className="filtros-filter-item filtros-filter-search" style={{ position: 'relative', width: '250px' }}>
                <FiSearch
                  size={16}
                  className="bi-search-input-icon"
                  style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#64748b', zIndex: 1, pointerEvents: 'none' }}
                />
                <input
                  type="text"
                  className="form-control-modern"
                  placeholder="Buscar filtro..."
                  value={searchTerm}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  style={{ paddingLeft: '38px', height: '40px' }}
                />
                {searchTerm && (
                  <button
                    type="button"
                    onClick={() => handleSearchChange('')}
                    style={{
                      position: 'absolute',
                      right: '10px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      color: '#94a3b8',
                      cursor: 'pointer',
                      padding: '2px',
                      display: 'flex',
                      alignItems: 'center'
                    }}
                    title="Limpar busca"
                  >
                    <FiX size={14} />
                  </button>
                )}
              </div>

              <div className="filtros-filter-item filtros-filter-branch" style={{ width: '220px', position: 'relative', zIndex: 110 }}>
                <MultiSelectSearch
                  label=""
                  placeholder="Filtrar filial..."
                  value={filterBranchSearch}
                  onChange={handleBranchChange}
                  fetchData={filterDataService.getFiliais}
                  badgeColor="#10b981"
                  showCodePrefix
                />
              </div>

              <div className="filtros-filter-item filtros-filter-vendor" style={{ width: '220px', position: 'relative', zIndex: 110 }}>
                <SingleFilterSelect
                  label=""
                  placeholder="Filtrar vendedor..."
                  value={filterVendorSearch}
                  onChange={handleVendorChange}
                  fetchData={filterDataService.getVendedores}
                  showCodePrefix
                />
              </div>

              <div className="filtros-filter-actions d-flex align-items-center gap-2 flex-wrap">
                {hasActiveFilters && (
                  <button
                    type="button"
                    className="btn-modern btn-modern-light btn-modern-sm"
                    onClick={handleClearAllFilters}
                    title="Limpar filtros"
                    style={{ height: '40px', padding: '0 12px' }}
                  >
                    <FiX size={14} />
                  </button>
                )}

                {canManage && selectedFilterIds.length > 0 && (
                  <button
                    type="button"
                    className="btn-modern btn-modern-light-danger btn-modern-sm animate__animated animate__fadeIn"
                    onClick={() => setShowBulkDeleteModal(true)}
                    style={{ height: '40px', padding: '0 14px' }}
                  >
                    <FiTrash2 size={14} className="me-1" />
                    Excluir ({selectedFilterIds.length})
                  </button>
                )}

                {totalRecords > 0 && (
                  <span className="badge-modern badge-modern-primary py-2 px-3 fw-semibold">
                    {totalRecords} filtro{totalRecords !== 1 ? 's' : ''}
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="card-body-modern p-4 pt-0" style={{ position: 'relative' }}>
            {loading && filteredList.length > 0 && (
              <Loading.Overlay text="Atualizando filtros..." size="sm" />
            )}
            <div className="table-responsive">
              <table className="table-modern w-100" style={{ minWidth: '680px' }}>
                <thead>
                  <tr>
                    <th style={{ width: '40px' }}>
                      <input
                        type="checkbox"
                        checked={filteredList.length > 0 && selectedFilterIds.length === filteredList.length}
                        onChange={() => toggleSelectAll(filteredList)}
                      />
                    </th>
                    <th style={{ width: '22%', textAlign: 'left', cursor: 'pointer' }} onClick={() => handleSort('name')}>Nome{renderSortIcon('name')}</th>
                    <th style={{ width: '22%', textAlign: 'left', cursor: 'pointer' }} onClick={() => handleSort('filter_branch')}>Filiais{renderSortIcon('filter_branch')}</th>
                    <th style={{ width: '22%', textAlign: 'left', cursor: 'pointer' }} onClick={() => handleSort('filter_vendor')}>Vendedores{renderSortIcon('filter_vendor')}</th>
                    <th style={{ width: '19%', textAlign: 'center', cursor: 'pointer' }} onClick={() => handleSort('period_start')}>Período{renderSortIcon('period_start')}</th>
                    <th style={{ width: '15%', textAlign: 'center' }}>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {loading && filteredList.length === 0 ? (
                    <Loading.Table
                      colSpan={6}
                      text="Carregando filtros..."
                      subtext="Buscando regras de produtos, marcas, departamentos e seções..."
                    />
                  ) : filteredList.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="text-center text-muted py-4">Nenhum filtro encontrado.</td>
                    </tr>
                  ) : (
                    filteredList.map((f) => (
                      <tr key={f.id}>
                        <td>
                          <input
                            type="checkbox"
                            checked={selectedFilterIds.includes(f.id)}
                            onChange={() => toggleSelectOne(f.id)}
                          />
                        </td>
                        <td className="fw-semibold text-dark" style={{ textAlign: 'left' }}>{f.name}</td>
                        <td className="text-muted" style={{ textAlign: 'left' }}>{f.filter_branch || '-'}</td>
                        <td className="text-muted" style={{ textAlign: 'left' }}>{f.filter_vendor || '-'}</td>
                        <td className="text-muted" style={{ textAlign: 'center' }}>
                          {f.start_date && f.end_date ? (
                            <>{String(f.start_date).slice(0, 10)} — {String(f.end_date).slice(0, 10)}</>
                          ) : (
                            '-'
                          )}
                        </td>
                        <td>
                          <div className="d-flex justify-content-end align-items-center gap-2">
                            {canManage ? (
                              <>
                                <button
                                  type="button"
                                  className="btn-modern btn-modern-light btn-modern-sm"
                                  onClick={() => replicate(f)}
                                  title="Replicar filtro"
                                  style={{ padding: '6px 10px' }}
                                >
                                  <FiCopy size={14} />
                                </button>
                                <button
                                  type="button"
                                  className="btn-modern btn-modern-light btn-modern-sm"
                                  onClick={() => edit(f)}
                                  title="Editar filtro"
                                  style={{ padding: '6px 10px' }}
                                >
                                  <FiEdit2 size={14} />
                                </button>
                                <button
                                  type="button"
                                  className="btn-modern btn-modern-light-danger btn-modern-sm"
                                  onClick={() => remove(f.id)}
                                  title="Excluir filtro"
                                  style={{ padding: '6px 10px' }}
                                >
                                  <FiTrash2 size={14} />
                                </button>
                              </>
                            ) : (
                              <button
                                type="button"
                                className="btn-modern btn-modern-light btn-modern-sm"
                                onClick={() => edit(f)}
                                title="Visualizar filtro"
                                style={{ padding: '6px 10px' }}
                              >
                                <FiSearch size={14} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {totalRecords > 0 && !searchTerm && (
              <BIPagination
                page={page}
                perPage={perPage}
                total={totalRecords}
                onPageChange={handlePageChange}
                onPerPageChange={(newPerPage) => {
                  setPerPage(newPerPage);
                  setPage(1);
                  load({ page: 1, per_page: newPerPage });
                }}
                itemLabel="filtros"
                perPageOptions={[10, 25, 50, 100, 200]}
              />
            )}
            {searchTerm && filteredList.length > 0 && (
              <div className="mt-3 px-2">
                <small className="text-muted">
                  Mostrando {filteredList.length} filtro{filteredList.length !== 1 ? 's' : ''} filtrado{filteredList.length !== 1 ? 's' : ''}
                </small>
              </div>
            )}
          </div>
        </div>
      </div>

        {/* Modal Novo/Editar Filtro */}
        {showModal && (
          <div
            style={{
              position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              zIndex: 9999, backdropFilter: 'blur(4px)', padding: '20px'
            }}
          >
            <div
              style={{
                background: 'white', borderRadius: '20px', width: '100%',
                maxWidth: '600px', maxHeight: '90vh',
                boxShadow: '0 24px 60px rgba(0,0,0,0.25)',
                animation: 'modalSlideIn 0.25s ease',
                display: 'flex', flexDirection: 'column',
                overflow: 'hidden'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div style={{
                padding: '24px 28px 20px',
                borderBottom: '1px solid var(--border)',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                background: 'white', zIndex: 10, flexShrink: 0
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{
                    width: '44px', height: '44px', borderRadius: '12px',
                    background: 'var(--primary)', color: 'white',
                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                  }}>
                    <FiFilter size={20} />
                  </div>
                  <div>
                    <h5 style={{ margin: 0, fontWeight: 700, fontSize: '1rem' }}>
                      {form.id ? 'Editar filtro' : 'Novo filtro'}
                    </h5>
                    <p style={{ margin: 0, fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                      {form.id ? 'Atualize os dados do filtro' : 'Selecione múltiplos códigos nos campos de filtro'}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={handleCloseModal}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', borderRadius: '8px' }}
                >
                  <FiX size={20} color="var(--text-muted)" />
                </button>
              </div>

              {/* Modal Body */}
              <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
                <div style={{ padding: '24px 28px', overflowY: 'auto', flex: 1 }}>
                  <div className="form-group-modern">
                    <label>Nome *</label>
                    <input
                      className="form-control-modern"
                      required
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      placeholder="Ex: Filtro Região Sul"
                    />
                  </div>
                  <div className="form-group-modern">
                    <label>Descrição</label>
                    <input
                      className="form-control-modern"
                      value={form.description}
                      onChange={(e) => setForm({ ...form, description: e.target.value })}
                      placeholder="Descrição opcional do filtro"
                    />
                  </div>
                  <MultiSelectSearch
                    label="Filiais"
                    placeholder="Buscar filial..."
                    value={form.filter_branch}
                    onChange={(val) => setForm({ ...form, filter_branch: val })}
                    fetchData={filterDataService.getFiliais}
                    badgeColor="#10b981"
                    showCodePrefix
                    required
                  />
                  <MultiSelectSearch
                    label="Departamentos"
                    placeholder="Buscar departamento..."
                    value={form.filter_department}
                    onChange={(val) => setForm({ ...form, filter_department: val })}
                    fetchData={filterDataService.getDepartamentos}
                    badgeColor="#8b5cf6"
                  />
                  <MultiSelectSearch
                    label="Atividades"
                    placeholder="Buscar atividade..."
                    value={form.filter_activity}
                    onChange={(val) => setForm({ ...form, filter_activity: val })}
                    fetchData={filterDataService.getAtividades}
                    badgeColor="#f59e0b"
                    showCodePrefix
                  />
                  <MultiSelectSearch
                    label="Seções"
                    placeholder="Buscar seção..."
                    value={form.filter_section}
                    onChange={(val) => setForm({ ...form, filter_section: val })}
                    onOptionSelected={(selectedSection) => {
                      if (selectedSection?.codepto !== undefined && selectedSection?.codepto !== null) {
                        setForm((prev) => {
                          const currentDepartments = parseCodes(prev.filter_department);
                          const deptCode = String(selectedSection.codepto);
                          
                          if (!currentDepartments.includes(deptCode)) {
                            return {
                              ...prev,
                              filter_department: mergeCodes(prev.filter_department, deptCode)
                            };
                          }
                          return prev;
                        });
                      }
                    }}
                    fetchData={filterDataService.getSecoes}
                    badgeColor="#14b8a6"
                    showCodePrefix
                  />
                  <MultiSelectSearch
                    label="Categorias"
                    placeholder="Buscar categoria..."
                    value={form.filter_category}
                    onChange={(val) => setForm({ ...form, filter_category: val })}
                    fetchData={(search, limit, offset) => 
                      filterDataService.getCategorias(
                        search, 
                        form.filter_section || '',
                        limit,
                        offset
                      )
                    }
                    badgeColor="#0ea5e9"
                    showCodePrefix
                    infiniteScroll
                  />
                  <MultiSelectSearch
                    label="Marcas"
                    placeholder="Buscar marca..."
                    value={form.filter_brand}
                    onChange={(val) => setForm({ ...form, filter_brand: val })}
                    fetchData={filterDataService.getMarcas}
                    badgeColor="#ec4899"
                    showCodePrefix
                  />
                  <MultiSelectSearch
                    label="Produtos"
                    placeholder="Buscar produto..."
                    value={form.filter_product_code}
                    onChange={(val) => setForm({ ...form, filter_product_code: val })}
                    fetchData={filterDataService.getProdutos}
                    badgeColor="#3b82f6"
                    infiniteScroll
                    formatLabel={(opt) => `${opt.codigo} - ${opt.descricao}`}
                  />
                  <MultiSelectSearch
                    label="Vendedores"
                    placeholder="Buscar vendedor..."
                    value={form.filter_vendor}
                    onChange={(val) => setForm({ ...form, filter_vendor: val })}
                    fetchData={filterDataService.getVendedores}
                    badgeColor="#8b5cf6"
                  />
                  <div className="form-group-modern mb-3">
                    <label style={{ marginBottom: '8px', display: 'block' }}>Comportamento de vendedor</label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', margin: 0 }}>
                      <input
                        type="checkbox"
                        checked={Boolean(form.inherit_vendor_from_goal)}
                        onChange={(e) => setForm({ ...form, inherit_vendor_from_goal: e.target.checked })}
                        style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                      />
                      <span style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>
                        Herdar vendedor da meta quando este filtro não tiver vendedor informado.
                      </span>
                    </label>
                  </div>
                  <EmployeeSelectSearch
                    label="Funcionários"
                    value={form.filter_employee}
                    onChange={(val) => setForm({ ...form, filter_employee: val })}
                  />
                  <div className="form-group-modern mb-3">
                    <label style={{ marginBottom: '8px', display: 'block' }}>Comportamento de funcionário</label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', margin: 0 }}>
                      <input
                        type="checkbox"
                        checked={Boolean(form.inherit_employee_from_goal)}
                        onChange={(e) => setForm({ ...form, inherit_employee_from_goal: e.target.checked })}
                        style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                      />
                      <span style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>
                        Herdar funcionário da meta quando este filtro não tiver funcionário informado.
                      </span>
                    </label>
                  </div>
                  <div className="form-group-modern mb-3">
                    <label style={{ marginBottom: '8px', display: 'block' }}>Comportamento de filial</label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', margin: 0 }}>
                      <input
                        type="checkbox"
                        checked={Boolean(form.inherit_branch_from_goal)}
                        onChange={(e) => setForm({ ...form, inherit_branch_from_goal: e.target.checked })}
                        style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                      />
                      <span style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>
                        Herdar filial do vendedor da meta quando este filtro não tiver filial informada.
                      </span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', margin: 0, marginTop: '8px' }}>
                      <input
                        type="checkbox"
                        checked={Boolean(form.inherit_branch_from_preset)}
                        onChange={(e) => setForm({ ...form, inherit_branch_from_preset: e.target.checked })}
                        style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                      />
                      <span style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>
                        Herdar filial do Grupo de Meta cadastrado quando este filtro não tiver filial informada.
                      </span>
                    </label>
                  </div>

                  {/* Período do Filtro */}
                  <div style={{ marginTop: '8px', marginBottom: '16px' }}>
                    <label style={{ display: 'block', fontWeight: 600, fontSize: '0.85rem', color: '#475569', marginBottom: '10px' }}>
                      Período do Filtro
                    </label>
                    <div className="row g-3">
                      <div className="col-6">
                        <div className="form-group-modern mb-0">
                          <label>Data início</label>
                          <input
                            type="date"
                            className="form-control-modern"
                            value={form.start_date}
                            onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                          />
                        </div>
                      </div>
                      <div className="col-6">
                        <div className="form-group-modern mb-0">
                          <label>Data fim</label>
                          <input
                            type="date"
                            className="form-control-modern"
                            value={form.end_date}
                            onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Regras de Adicional */}
                  <div style={{ marginBottom: '8px' }}>
                    <label style={{ display: 'block', fontWeight: 600, fontSize: '0.85rem', color: '#475569', marginBottom: '10px' }}>
                      Regras de Adicional
                    </label>
                    <div className="row g-3">
                      <div className="col-md-4">
                        <div className="form-group-modern mb-0">
                          <label>Qtd. mínima produtos</label>
                          <input
                            type="number"
                            step="1"
                            min="0"
                            className="form-control-modern"
                            value={form.commission_min_products_qty}
                            onChange={(e) => setForm({ ...form, commission_min_products_qty: e.target.value })}
                            placeholder="Ex: 100"
                          />
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="form-group-modern mb-0">
                          <label>Valor mínimo vendido (R$)</label>
                          <input
                            type="text"
                            className="form-control-modern"
                            value={form.commission_use_goal_value ? '' : form.commission_min_sold_value}
                            onChange={(e) => setForm({ ...form, commission_min_sold_value: formatCurrencyBRL(e.target.value) })}
                            placeholder={form.commission_use_goal_value ? "Usando Valor da Meta" : "R$ 0,00"}
                            disabled={form.commission_use_goal_value}
                          />
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="form-group-modern mb-0">
                          <label>Adicional (%)</label>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            className="form-control-modern"
                            value={form.commission_percent}
                            onChange={(e) => setForm({ ...form, commission_percent: e.target.value })}
                            placeholder="Ex: 2.50"
                          />
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="form-group-modern mb-0">
                          <label>Valor fixo por unidade (R$)</label>
                          <input
                            type="text"
                            className="form-control-modern"
                            value={form.commission_fixed_per_unit}
                            onChange={(e) => setForm({ ...form, commission_fixed_per_unit: formatCurrencyBRL(e.target.value) })}
                            placeholder="R$ 0,00"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="row mt-2">
                      <div className="col-md-6">
                        <div className="form-check-modern" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <input
                            type="checkbox"
                            id="commission_use_goal_value"
                            className="form-check-input"
                            checked={form.commission_use_goal_value}
                            onChange={(e) => setForm({ ...form, commission_use_goal_value: e.target.checked })}
                            style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                          />
                          <label className="form-check-label" htmlFor="commission_use_goal_value" style={{ cursor: 'pointer', fontSize: '0.85rem' }}>
                            Usar Valor da Meta como limite mínimo vendido
                          </label>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="form-check-modern" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <input
                            type="checkbox"
                            id="show_commission_percentage"
                            className="form-check-input"
                            checked={form.show_commission_percentage}
                            onChange={(e) => setForm({ ...form, show_commission_percentage: e.target.checked })}
                            style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                          />
                          <label className="form-check-label" htmlFor="show_commission_percentage" style={{ cursor: 'pointer', fontSize: '0.85rem' }}>
                            Exibir porcentagem no relatório
                          </label>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="form-check-modern" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <input
                            type="checkbox"
                            id="include_in_goal_total"
                            className="form-check-input"
                            checked={form.include_in_goal_total}
                            onChange={(e) => setForm({ ...form, include_in_goal_total: e.target.checked })}
                            style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                          />
                          <label className="form-check-label" htmlFor="include_in_goal_total" style={{ cursor: 'pointer', fontSize: '0.85rem' }}>
                            Incluir no total da meta
                          </label>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="form-check-modern" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <input
                            type="checkbox"
                            id="include_commission_in_goal_total"
                            className="form-check-input"
                            checked={form.include_commission_in_goal_total}
                            onChange={(e) => setForm({ ...form, include_commission_in_goal_total: e.target.checked })}
                            style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                          />
                          <label className="form-check-label" htmlFor="include_commission_in_goal_total" style={{ cursor: 'pointer', fontSize: '0.85rem' }}>
                            Incluir adicional na meta
                          </label>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="form-check-modern" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <input
                            type="checkbox"
                            id="include_value_if_min_achieved"
                            className="form-check-input"
                            checked={form.include_value_if_min_achieved}
                            onChange={(e) => setForm({ ...form, include_value_if_min_achieved: e.target.checked })}
                            style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                          />
                          <label className="form-check-label" htmlFor="include_value_if_min_achieved" style={{ cursor: 'pointer', fontSize: '0.85rem' }} title="Somente inclui o valor vendido deste filtro na meta se atingir a meta mínima (Qtd Mín ou Valor Mín)">
                            Somar valor se atingir meta mín. (Qtd/Valor)
                          </label>
                        </div>
                      </div>
                      <div className="col-md-12">
                        <div className="form-check-modern" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <input
                            type="checkbox"
                            id="available_for_goals"
                            className="form-check-input"
                            checked={form.available_for_goals}
                            onChange={(e) => setForm({ ...form, available_for_goals: e.target.checked })}
                            style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                          />
                          <label className="form-check-label" htmlFor="available_for_goals" style={{ cursor: 'pointer', fontSize: '0.85rem' }}>
                            <strong>Disponível para Metas</strong> (permite selecionar este filtro no cadastro e edição de metas)
                          </label>
                        </div>
                      </div>
                    </div>

                  </div>

                  {/* Valor de Recompensa (Bonus) */}
                  <div style={{ marginBottom: '8px' }}>
                    <label style={{ display: 'block', fontWeight: 600, fontSize: '0.85rem', color: '#475569', marginBottom: '10px' }}>
                      Valor de Recompensa (Bônus)
                    </label>
                    <p className="text-muted small mb-3">Configure regras para pagamento de bônus quando o vendedor atingir as condições abaixo (OU).</p>
                    <div className="row g-3">
                      <div className="col-md-4">
                        <div className="form-group-modern mb-0">
                          <label>Qtd. mínima produtos</label>
                          <input
                            type="number"
                            step="1"
                            min="0"
                            className="form-control-modern"
                            value={form.bonus_min_products_qty}
                            onChange={(e) => setForm({ ...form, bonus_min_products_qty: e.target.value })}
                            placeholder="Ex: 100"
                          />
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="form-group-modern mb-0">
                          <label>Valor mínimo vendido (R$)</label>
                          <input
                            type="text"
                            className="form-control-modern"
                            value={form.bonus_min_sold_value}
                            onChange={(e) => setForm({ ...form, bonus_min_sold_value: formatCurrencyBRL(e.target.value) })}
                            placeholder="R$ 0,00"
                          />
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="form-group-modern mb-0">
                          <label>Valor do Bônus (R$)</label>
                          <input
                            type="text"
                            className="form-control-modern"
                            value={form.bonus_amount}
                            onChange={(e) => setForm({ ...form, bonus_amount: formatCurrencyBRL(e.target.value) })}
                            placeholder="R$ 0,00"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Modal Footer */}
                <div style={{
                  padding: '16px 28px 24px',
                  borderTop: '1px solid var(--border)',
                  display: 'flex', justifyContent: 'flex-end', gap: '12px',
                  background: 'white', zIndex: 10, flexShrink: 0
                }}>
                  <button
                    type="button"
                    className="btn-modern btn-modern-secondary"
                    onClick={handleCloseModal}
                    disabled={loading}
                  >
                    {canManage ? 'Cancelar' : 'Fechar'}
                  </button>
                  {canManage && (
                    <button
                      type="submit"
                      className="btn-modern btn-modern-primary"
                      disabled={loading}
                    >
                      {loading ? 'Salvando...' : form.id ? 'Atualizar' : <><FiSave size={16} className="me-1" />Salvar</>}
                    </button>
                  )}
                </div>
              </form>
            </div>
          </div>
        )}

      {/* Modal de Confirmação de Exclusão */}
      {showBulkDeleteModal && (
        <div
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 9999, backdropFilter: 'blur(4px)', padding: '20px'
          }}
          onClick={(e) => e.target === e.currentTarget && setShowBulkDeleteModal(false)}
        >
          <div style={{
            background: 'white', borderRadius: '20px',
            width: '100%', maxWidth: '450px',
            boxShadow: '0 24px 60px rgba(0,0,0,0.25)',
            animation: 'modalSlideIn 0.25s ease',
            overflow: 'hidden'
          }}>
            <div style={{ padding: '32px 28px 20px', textAlign: 'center' }}>
              <div style={{
                width: '64px', height: '64px', borderRadius: '50%',
                background: '#fee2e2', color: '#dc2626',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                margin: '0 auto 16px'
              }}>
                <FiTrash2 size={32} />
              </div>
              <h4 style={{ margin: '0 0 8px', fontWeight: 700, fontSize: '1.25rem', color: '#1e293b' }}>
                Excluir Filtros
              </h4>
              <p style={{ margin: 0, fontSize: '0.9rem', color: '#64748b' }}>
                Tem certeza que deseja excluir <strong>{selectedFilterIds.length}</strong> filtros selecionados?
              </p>
            </div>

            <div style={{ padding: '0 28px 20px', textAlign: 'center' }}>
              <div style={{ padding: '12px 16px', background: '#fef3c7', border: '1px solid #fbbf24', borderRadius: '8px', fontSize: '0.85rem', color: '#92400e' }}>
                ⚠️ Esta ação não pode ser desfeita e removerá permanentemente os filtros.
              </div>
            </div>

            <div style={{ padding: '16px 28px 24px', display: 'flex', justifyContent: 'center', gap: '12px' }}>
              <button
                type="button"
                className="btn-modern btn-modern-secondary"
                onClick={() => setShowBulkDeleteModal(false)}
                disabled={bulkDeleting}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="btn-modern btn-modern-danger"
                onClick={confirmBulkDelete}
                disabled={bulkDeleting}
              >
                <FiTrash2 className="me-2" size={16} />
                {bulkDeleting ? 'Excluindo...' : 'Sim, Excluir'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showDeleteModal && filterToDelete && (
        <div
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 9999, backdropFilter: 'blur(4px)', padding: '20px'
          }}
          onClick={(e) => e.target === e.currentTarget && setShowDeleteModal(false)}
        >
          <div style={{
            background: 'white', borderRadius: '20px',
            width: '100%', maxWidth: '450px',
            boxShadow: '0 24px 60px rgba(0,0,0,0.25)',
            animation: 'modalSlideIn 0.25s ease',
            overflow: 'hidden'
          }}>
            {/* Ícone de Aviso */}
            <div style={{
              padding: '32px 28px 20px',
              textAlign: 'center'
            }}>
              <div style={{
                width: '64px', height: '64px', borderRadius: '50%',
                background: '#fee2e2', color: '#dc2626',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                margin: '0 auto 16px'
              }}>
                <FiAlertTriangle size={32} />
              </div>
              <h4 style={{ margin: '0 0 8px', fontWeight: 700, fontSize: '1.25rem', color: '#1e293b' }}>
                Confirmar Exclusão
              </h4>
              <p style={{ margin: 0, fontSize: '0.9rem', color: '#64748b' }}>
                Tem certeza que deseja excluir o filtro
              </p>
              <p style={{ margin: '8px 0 0', fontWeight: 600, fontSize: '1rem', color: '#1e293b' }}>
                "{filterToDelete.name}"?
              </p>
            </div>

            {/* Alerta */}
            <div style={{
              padding: '0 28px 20px',
              textAlign: 'center'
            }}>
              <div style={{
                padding: '12px 16px',
                background: '#fef3c7',
                border: '1px solid #fbbf24',
                borderRadius: '8px',
                fontSize: '0.85rem',
                color: '#92400e'
              }}>
                ⚠️ Esta ação não pode ser desfeita. Todos os dados do filtro serão removidos.
              </div>
            </div>

            {/* Footer */}
            <div style={{
              padding: '16px 28px 24px',
              display: 'flex', justifyContent: 'center', gap: '12px'
            }}>
              <button
                type="button"
                className="btn-modern btn-modern-secondary"
                onClick={() => setShowDeleteModal(false)}
                disabled={deleting}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="btn-modern btn-modern-danger"
                onClick={confirmDelete}
                disabled={deleting}
              >
                <FiTrash2 className="me-2" size={16} />
                {deleting ? 'Excluindo...' : 'Sim, Excluir'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Erro */}
      {showErrorModal && (
        <div
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 9999, backdropFilter: 'blur(4px)', padding: '20px'
          }}
          onClick={(e) => e.target === e.currentTarget && setShowErrorModal(false)}
        >
          <div style={{
            background: 'white', borderRadius: '20px',
            width: '100%', maxWidth: '450px',
            boxShadow: '0 24px 60px rgba(0,0,0,0.25)',
            animation: 'modalSlideIn 0.25s ease',
            overflow: 'hidden'
          }}>
            {/* Ícone de Erro */}
            <div style={{
              padding: '32px 28px 20px',
              textAlign: 'center'
            }}>
              <div style={{
                width: '64px', height: '64px', borderRadius: '50%',
                background: '#fee2e2', color: '#dc2626',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                margin: '0 auto 16px'
              }}>
                <FiAlertTriangle size={32} />
              </div>
              <h4 style={{ margin: '0 0 8px', fontWeight: 700, fontSize: '1.25rem', color: '#1e293b' }}>
                Erro ao Salvar
              </h4>
              <p style={{ margin: 0, fontSize: '0.9rem', color: '#64748b', lineHeight: '1.5' }}>
                {errorMessage}
              </p>
            </div>

            {/* Footer */}
            <div style={{
              padding: '16px 28px 24px',
              display: 'flex', justifyContent: 'center', gap: '12px'
            }}>
              <button
                type="button"
                className="btn-modern btn-modern-primary"
                onClick={() => setShowErrorModal(false)}
              >
                Entendido
              </button>
            </div>
          </div>
        </div>
      )}

        <style>{`
          .spin-ico { animation: spin-ico 0.8s linear infinite; display: inline-block; }
          @keyframes spin-ico { to { transform: rotate(360deg); } }
          @keyframes modalSlideIn { from { opacity: 0; transform: translateY(-20px); } to { opacity: 1; transform: translateY(0); } }
        `}</style>
    </div>
  );
}

export default Filtros;
